import { useState, useEffect, useRef, useCallback } from 'react';
import { rhythmRatio as computeRhythmRatio, smoothRhythmRatio } from '../lib/rhythmRatio';
import { BandPassFilter } from '../lib/bandPassFilter';
import { createStrokeCounter } from '../lib/strokeCounter';
import {
  createRowingDetector, createGpsCheck, phoneAutocorr, peakToPeak, PHONE_WINDOW_S,
} from '../lib/rowingDetector';

/**
 * Hybrid Stroke Rate Detection Algorithm
 * 
 * Combines:
 * 1. Autocorrelation method - for stable/steady state (robust, no false positives)
 * 2. Zero-crossing with adaptive threshold - for sprint/rate changes (fast response < 1s)
 * 3. GPS speed variance validation - reject if accel & GPS disagree
 */

// Configuration
const SAMPLE_RATE = 60; // Hz - typical device motion rate
const BUFFER_SECONDS = 4; // case / PM5 hybrid window (last 4 s of the buffer)
const HYBRID_SIZE = SAMPLE_RATE * BUFFER_SECONDS;
const BUFFER_SIZE = SAMPLE_RATE * PHONE_WINDOW_S; // 8 s kept (phone rowing-detector window)

// Stroke rate bounds (extended rowing range per user request)
const MIN_STROKE_RATE = 5; // spm (very slow/recovery)
const MAX_STROKE_RATE = 85; // spm (sprint/race start)
const MIN_PERIOD = 60 / MAX_STROKE_RATE; // seconds (~0.7s at 85 spm)
const MAX_PERIOD = 60 / MIN_STROKE_RATE; // seconds (12s at 5 spm)

// Zero-crossing filter - wider band to capture full range
// IIR filter cut-offs.
// Mode 2 (phone-only) uses a tighter upper cut-off because the phone has no
// IMU ground truth and the seat/body secondary oscillation at 20 spm sits at
// ~1.33 Hz — JUST inside the wider 1.5 Hz cut-off and produces double zero
// crossings per stroke. 0.9 Hz caps the band at ~54 spm, which is sufficient
// for on-water rowing.
const ZC_LOW_CUTOFF = 0.08;        // Hz (~5 spm)
const ZC_HIGH_CUTOFF = 1.5;        // Hz (~90 spm) — used in Mode 1 / 3
const ZC_HIGH_CUTOFF_PHONE = 0.9;  // Hz (~54 spm) — Mode 2, cleaner signal

// Phase-detection upper cutoff — wider than the rate-detection band so the
// sharp catch impulse passes through cleanly. Catches contain harmonic content
// well above the stroke fundamental (a catch at 24 spm = 0.4 Hz still has
// significant energy out to ~2 Hz). The rate detector uses a narrower band
// because it only cares about the fundamental crossing count; the phase
// detector needs accurate timing of the impulse, so it gets its own filter.
const PHASE_HIGH_CUTOFF = 2.5;     // Hz — uniform across all modes for RHY

// Thresholds
// Brief G — User reported phone IMU stroke detection reading ~1 SPM high on
// slower pieces. Raised from 0.8 → 0.95 to filter out marginal secondary
// recovery-phase peaks that double-count at slow rates without affecting
// the strong primary catches at race-pace pieces.
const MOTION_THRESHOLD = 0.95; // minimum acceleration to consider as motion

// Phone mode rate / rowing detector: lib/rowingDetector.js (29 Oct on-water settings).
const CASE_AC_THRESHOLD = 0.15;
// GPS speed-variance threshold. At 25 Hz CrewZ GPS we can sensibly use
// 0.5 m/s as a within-stroke disagreement bound; at 1 Hz phone GPS that
// number is meaningless (each sample bridges most of one stroke), so the
// check is effectively DISABLED in Mode 2 by setting an unreachable threshold.
const GPS_VARIANCE_THRESHOLD_DEFAULT = 0.5;
const GPS_VARIANCE_THRESHOLD_PHONE_ONLY = 999;

/**
 * Calculate autocorrelation of a signal
 * Returns array of correlation values for different lags
 */
function autocorrelate(signal) {
  const n = signal.length;
  if (n < 2) return [];
  
  // Normalize signal (subtract mean)
  const mean = signal.reduce((a, b) => a + b, 0) / n;
  const normalized = signal.map(v => v - mean);
  
  // Calculate variance for normalization
  const variance = normalized.reduce((sum, v) => sum + v * v, 0);
  if (variance === 0) return [];
  
  const result = [];
  const maxLag = Math.min(n - 1, Math.floor(MAX_PERIOD * SAMPLE_RATE));
  
  for (let lag = 0; lag <= maxLag; lag++) {
    let sum = 0;
    for (let i = 0; i < n - lag; i++) {
      sum += normalized[i] * normalized[i + lag];
    }
    result.push(sum / variance);
  }
  
  return result;
}

/**
 * Find the first significant peak in autocorrelation after lag 0
 * This represents the dominant period of the signal
 */
function findAutocorrelationPeriod(autocorr, sampleRate, threshold = CASE_AC_THRESHOLD, maxRate = MAX_STROKE_RATE, out = null) {
  if (autocorr.length < 20) return null;
  
  const minLag = Math.floor((60 / maxRate) * sampleRate); // samples at 60Hz
  const maxLag = Math.min(autocorr.length - 1, Math.floor(MAX_PERIOD * sampleRate));
  
  // Find first significant peak after minimum lag
  let peakLag = -1;
  let peakValue = 0;
  
  for (let i = minLag; i < maxLag - 1; i++) {
    // Check if this is a local maximum
    if (autocorr[i] > autocorr[i - 1] && autocorr[i] > autocorr[i + 1]) {
      // Must be above threshold (lowered for better sensitivity)
      if (autocorr[i] > threshold && autocorr[i] > peakValue) {
        peakLag = i;
        peakValue = autocorr[i];
        break; // Take first significant peak
      }
    }
  }
  
  if (out) out.peak = peakValue;
  if (peakLag === -1) return null;
  
  // Convert lag to period in seconds
  const period = peakLag / sampleRate;
  return period;
}

/**
 * Count zero crossings (positive) in filtered signal
 */
function countZeroCrossings(signal, timestamps) {
  if (signal.length < 2) return { count: 0, rate: 0 };
  
  let crossings = 0;
  let lastCrossingTime = timestamps[0];
  const intervals = [];
  
  for (let i = 1; i < signal.length; i++) {
    // Positive zero crossing (signal goes from negative to positive)
    if (signal[i - 1] < 0 && signal[i] >= 0) {
      crossings++;
      if (crossings > 1) {
        const interval = (timestamps[i] - lastCrossingTime) / 1000;
        if (interval > MIN_PERIOD && interval < MAX_PERIOD) {
          intervals.push(interval);
        }
      }
      lastCrossingTime = timestamps[i];
    }
  }
  
  if (intervals.length < 2) return { count: crossings, rate: 0 };
  
  // Use median interval for robustness. Round to the nearest HALF stroke
  // (e.g. 18.5 spm) so the display layer can render the ½ glyph. The
  // ClickableMetric ½-rendering path already snaps to half steps; emitting
  // .5-resolution here gives it real fractional data instead of pre-rounded
  // integers (which produced "18 SPM" with no fraction in Mode 2).
  intervals.sort((a, b) => a - b);
  const medianInterval = intervals[Math.floor(intervals.length / 2)];
  const rate = Math.round((60 / medianInterval) * 2) / 2;
  
  return { count: crossings, rate: Math.max(MIN_STROKE_RATE, Math.min(MAX_STROKE_RATE, rate)) };
}

/**
 * Calculate GPS speed variance over recent samples
 */
function calculateSpeedVariance(speedHistory) {
  if (speedHistory.length < 3) return 0;
  
  const recent = speedHistory.slice(-10);
  const mean = recent.reduce((a, b) => a + b, 0) / recent.length;
  const variance = recent.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / recent.length;
  
  return Math.sqrt(variance); // Return standard deviation
}

/**
 * Main Hook - Hybrid Stroke Detection
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.crewzConnected] — CrewZ case is streaming IMU data
 * @param {boolean} [opts.pm5Connected]   — PM5 BLE is connected
 *
 * Mode-aware precedence:
 *   • Mode 1 / 3 (CrewZ or PM5 present): hybrid autocorrelation + zero-cross
 *     blend (overridden by the CrewZ IMU passthrough when 'crewz-imu-update'
 *     events arrive).
 *   • Mode 2 (phone-only): autocorrelation ONLY. Zero-crossing is unreliable
 *     at the phone's effective sampling conditions (1 Hz GPS reference, no
 *     IMU ground truth) and causes false-positive rate spikes.
 */
export function useStrokeDetection({ crewzConnected = false, pm5Connected = false, paused = false } = {}) {
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const strokeCounterRef = useRef(createStrokeCounter());
  const isPhoneOnly = !crewzConnected && !pm5Connected;
  const isPhoneOnlyRef = useRef(isPhoneOnly);
  useEffect(() => { isPhoneOnlyRef.current = isPhoneOnly; }, [isPhoneOnly]);
  const [strokeRate, setStrokeRate] = useState(0);
  const [totalStrokes, setTotalStrokes] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  // Read-only mirror of the stroke rate for the Acceleration Curve graph's catch search.
  const strokeRateForGraphRef = useRef(0);
  useEffect(() => { strokeRateForGraphRef.current = strokeRate; }, [strokeRate]);
  const [hasMotionSensors, setHasMotionSensors] = useState(false);
  const [debugInfo, setDebugInfo] = useState({ 
    method: 'none', 
    signal: 0, 
    autocorrPeriod: 0,
    zcRate: 0 
  });
  
  // Use ref to track isTracking state for event handlers
  const isTrackingRef = useRef(false);
  
  // Data buffers
  const accelBufferRef = useRef([]);
  const timestampBufferRef = useRef([]);
  const filteredBufferRef = useRef([]);
  const gpsSpeedHistoryRef = useRef([]);
  
  // Filter instance
  const bandPassFilterRef = useRef(null);
  // Dedicated phase-detection filter — wider band so catch impulses survive
  // intact for accurate drive/recovery timing.
  const phaseFilterRef = useRef(null);
  // Adaptive amplitude tracker for hysteresis — without this the phase
  // detector double-fires on small ripples around zero, which destroys the
  // drive/recovery timing on noisy phone IMU data.
  const phaseAmpEnvelopeRef = useRef(0);
  
  // Stroke tracking
  const strokeCountRef = useRef(0);
  const lastStrokeTimeRef = useRef(0);
  const usingGravityRef = useRef(false);
  const detectorRef = useRef(createRowingDetector());
  const gpsCheckRef = useRef(createGpsCheck());
  const lastRateUpdateRef = useRef(0);

  // ── Rhythm Ratio (RHY) phase-tracking refs ────────────────────────────────
  // The band-pass filtered forward-acceleration signal crosses zero twice per
  // stroke: positive crossing = CATCH (drive starts), negative crossing =
  // FINISH (recovery starts). We keep the last seen timestamps so the
  // emit loop can derive drive/recovery durations and forward them as a
  // `crewz-rhythm-update` window event for the workout context to pick up.
  const lastCatchTRef = useRef(0);          // ms — last positive crossing
  const lastFinishTRef = useRef(0);         // ms — last negative crossing
  const lastEmittedCatchTRef = useRef(0);   // ms — guard against re-emitting
  const recentStrokesRef = useRef([]);      // rolling window of {drive, recovery}
  const prevFilteredRef = useRef(0);        // last filtered sample for crossing detection
  
  // Device orientation tracking
  const isLandscapeRef = useRef(window.innerWidth > window.innerHeight);
  
  // Keep ref in sync with state
  useEffect(() => {
    isTrackingRef.current = isTracking;
  }, [isTracking]);
  
  // Track device orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      isLandscapeRef.current = isLandscape;
      console.log(`📱 Orientation changed: ${isLandscape ? 'LANDSCAPE' : 'PORTRAIT'}`);
    };
    
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', () => {
      // Delay to let browser update dimensions
      setTimeout(handleOrientationChange, 100);
    });
    
    if (screen.orientation) {
      screen.orientation.addEventListener('change', () => {
        setTimeout(handleOrientationChange, 100);
      });
    }
    
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);
  
  // Initialize filter — high cut-off depends on mode (phone-only uses tighter
  // cap to suppress secondary body/seat oscillation that produces double
  // zero-crossings per stroke).
  useEffect(() => {
    const high = isPhoneOnly ? ZC_HIGH_CUTOFF_PHONE : ZC_HIGH_CUTOFF;
    bandPassFilterRef.current = new BandPassFilter(ZC_LOW_CUTOFF, high, SAMPLE_RATE);
    // Phase filter uses a wider, mode-independent band so the catch impulse
    // isn't blurred — critical for RHY working in Mode 2.
    phaseFilterRef.current = new BandPassFilter(ZC_LOW_CUTOFF, PHASE_HIGH_CUTOFF, SAMPLE_RATE);
    phaseAmpEnvelopeRef.current = 0;
  }, [isPhoneOnly]);

  // ST = floor(running total of rate ÷ 60 × seconds), not while paused.
  const countStrokes = (now, rate) => {
    const st = strokeCounterRef.current.update(now, rate, pausedRef.current);
    if (st !== strokeCountRef.current) {
      strokeCountRef.current = st;
      setTotalStrokes(st);
    }
  };

  /**
   * Process motion data using hybrid algorithm
   */
  const processMotionData = useCallback(() => {
    if (accelBufferRef.current.length < SAMPLE_RATE * 2) return; // Need at least 2 seconds

    if (isPhoneOnlyRef.current) {
      // ── Mode 2 (Phone Only): rowing detector on the 8 s band-pass window ─────
      // The rowing detector's 6 s close rule replaces the 1 s stationary check here.
      const now = Date.now();
      const win = filteredBufferRef.current;
      const { peak, rate } = phoneAutocorr(win, SAMPLE_RATE);
      const p2p = peakToPeak(win);
      const gpsHigh = gpsCheckRef.current.high(now);
      const step = detectorRef.current.step({ t: now, rate, acPeak: peak, p2p, gpsHigh });
      setStrokeRate(step.rate);
      countStrokes(now, step.rate);
      const gpsUsed = gpsCheckRef.current.lastUsed(now);
      window.dispatchEvent(new CustomEvent('crewz-rowing-detector', { detail: { t: now, open: step.open, show: step.show, rate: step.rate, acPeak: peak, p2p, gpsGood: gpsHigh, gpsSpeed: gpsUsed?.speed ?? null, gpsAccuracy: gpsUsed?.accuracy ?? null, candidate: rate, reasons: step.reasons } }));
      setDebugInfo({ method: step.show ? 'autocorr-phone-detected' : (step.open ? 'detector-confirming' : 'detector-closed'), autocorrRate: rate, acPeak: peak.toFixed(2), p2p: p2p.toFixed(2), gpsHigh, reasons: step.reasons.join(','), isPhoneOnly: true, bufferSize: win.length });
      return;
    }

    // Case / PM5 hybrid: last 4 s only (unchanged behaviour)
    const accelBuffer = accelBufferRef.current.slice(-HYBRID_SIZE);
    const timestamps = timestampBufferRef.current.slice(-HYBRID_SIZE);
    const filteredBuffer = filteredBufferRef.current.slice(-HYBRID_SIZE);
    const gpsSpeedHistory = gpsSpeedHistoryRef.current;
    
    // Check if there's enough motion
    const recentAccel = accelBuffer.slice(-SAMPLE_RATE);
    // With gravity only, remove the mean first so the gravity component can't fake motion.
    const recentMean = usingGravityRef.current ? recentAccel.reduce((a, b) => a + b, 0) / recentAccel.length : 0;
    const avgMagnitude = recentAccel.reduce((a, b) => a + Math.abs(b - recentMean), 0) / recentAccel.length;
    
    if (avgMagnitude < MOTION_THRESHOLD) {
      // Stationary - decay to zero
      setStrokeRate(0);
      setDebugInfo(prev => ({ 
        ...prev, 
        method: 'stationary', 
        signal: avgMagnitude.toFixed(2),
        rawAutocorr: 0,
        rawZc: 0
      }));
      return;
    }
    
    // Calculate GPS speed variance to determine which method to use.
    // Mode-aware threshold: 0.5 m/s in CrewZ/PM5 mode (25 Hz GPS makes the
    // number meaningful), effectively disabled in Mode 2 phone-only mode
    // (1 Hz GPS variance is meaningless within a single stroke).
    const speedVariance = calculateSpeedVariance(gpsSpeedHistory);
    const gpsVarianceThreshold = isPhoneOnlyRef.current
      ? GPS_VARIANCE_THRESHOLD_PHONE_ONLY
      : GPS_VARIANCE_THRESHOLD_DEFAULT;
    const gpsVarianceOk = speedVariance < gpsVarianceThreshold;
    
    // Method 1: Autocorrelation (for steady state) — keep half-spm resolution
    // so the display can render the ½ glyph.
    const autocorr = autocorrelate(accelBuffer);
    const autocorrPeriod = findAutocorrelationPeriod(autocorr, SAMPLE_RATE, CASE_AC_THRESHOLD, MAX_STROKE_RATE);
    const rawAutocorrRate = autocorrPeriod ? Math.round((60 / autocorrPeriod) * 2) / 2 : 0;
    // Clamp to valid range
    const autocorrRate = rawAutocorrRate > 0 ? Math.max(MIN_STROKE_RATE, Math.min(MAX_STROKE_RATE, rawAutocorrRate)) : 0;
    
    // Method 2: Zero-crossing (for fast response)
    const zcResult = countZeroCrossings(filteredBuffer, timestamps);
    const rawZcRate = zcResult.rate;
    // Note: zcResult.rate is already clamped in countZeroCrossings
    const zcRate = rawZcRate;
    
    // Hybrid decision logic - optimized for accuracy at all stroke rates
    let finalRate = 0;
    let method = 'none';

    {
      // ── Mode 1 / 3 (CrewZ or PM5 connected) ─────────────────────────────
      // Hybrid blend — note that when the CrewZ IMU passthrough is streaming
      // it overrides this value entirely further down the file.
      //
      // At higher stroke rates (>25 spm), zero-crossing is more responsive.
      // At lower rates (<25 spm), autocorrelation is more stable.
      const isHighRate = (zcRate > 25 || autocorrRate > 25);

      if (autocorrRate > 0 && zcRate > 0) {
        // Both methods have results
        const diff = Math.abs(autocorrRate - zcRate);

        // Keep half-spm resolution in the blends so Mode 1/3 also benefit
        // from the ½ display (snaps to nearest 0.5 after the weighted mean).
        const halfRound = (v) => Math.round(v * 2) / 2;

        if (diff <= 4) {
          // Methods closely agree - average them
          finalRate = halfRound((autocorrRate + zcRate) / 2);
          method = 'hybrid';
        } else if (diff <= 8) {
          // Moderate disagreement - weight based on stroke rate regime
          if (isHighRate) {
            // At high rates, favor zero-crossing (faster response)
            finalRate = halfRound(autocorrRate * 0.3 + zcRate * 0.7);
            method = 'hybrid-zc';
          } else {
            // At low rates, favor autocorrelation (more stable)
            finalRate = halfRound(autocorrRate * 0.7 + zcRate * 0.3);
            method = 'hybrid-ac';
          }
        } else {
          // Large disagreement - use the method appropriate for current regime
          if (isHighRate) {
            finalRate = zcRate;
            method = 'zero-cross';
          } else {
            finalRate = autocorrRate;
            method = 'autocorr';
          }
        }
      } else if (zcRate > 0) {
        // Only zero-crossing has result - use it (faster response)
        finalRate = zcRate;
        method = 'zero-cross';
      } else if (autocorrRate > 0) {
        // Only autocorrelation has result
        finalRate = autocorrRate;
        method = 'autocorr';
      } else if (rawAutocorrRate > 0) {
        // Raw autocorr was outside bounds but detected something
        finalRate = Math.max(MIN_STROKE_RATE, Math.min(MAX_STROKE_RATE, rawAutocorrRate));
        method = 'autocorr-clamped';
      }
    }
    
    // Update stroke rate state
    setStrokeRate(finalRate);
    
    // ST: running total of rate × time (lib/strokeCounter.js)
    countStrokes(Date.now(), finalRate);
    
    // Update debug info with all raw values for troubleshooting
    setDebugInfo({
      method,
      signal: avgMagnitude.toFixed(2),
      autocorrPeriod: autocorrPeriod?.toFixed(2) || 0,
      autocorrRate,
      rawAutocorr: rawAutocorrRate,
      zcRate,
      rawZc: rawZcRate,
      zcCount: zcResult.count,
      speedVariance: speedVariance.toFixed(2),
      gpsVarianceOk,
      isPhoneOnly: isPhoneOnlyRef.current,
      orientation: isLandscapeRef.current ? 'landscape' : 'portrait',
      axis: isLandscapeRef.current ? 'X' : 'Y',
      bufferSize: accelBuffer.length
    });

    // Optional: Fix-3 instrumentation behind ?debug=1 URL flag.
    // Confirms the /2 divisor in zero-crossing rate maths is correct
    // (one stroke should produce exactly 2 zero crossings).
    try {
      if (typeof window !== 'undefined' &&
          window.location?.search?.includes('debug=1') &&
          finalRate > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `[stroke-debug] method=${method} rate=${finalRate}spm ` +
          `zcCount=${zcResult.count} zcRate=${zcRate} ` +
          `autocorr=${autocorrRate} variance=${speedVariance.toFixed(2)} ` +
          `gpsVariance=${gpsVarianceOk ? 'open' : 'closed'} phoneOnly=${isPhoneOnlyRef.current}`,
        );
      }
    } catch {}
    
  }, []);

  /**
   * Handle device motion event
   */
  const handleMotion = useCallback((event) => {
    if (!isTrackingRef.current) return;
    
    const now = Date.now();
    
    // Get accelerometer data
    let accel = event.acceleration;
    if (!accel || accel.x === null) {
      accel = event.accelerationIncludingGravity;
      usingGravityRef.current = true;
    } else {
      usingGravityRef.current = false;
    }
    
    if (!accel) return;
    
    const ax = accel.x || 0;
    const ay = accel.y || 0;
    const az = accel.z || 0;
    
    // Select the correct axis based on device orientation:
    // - Portrait mode: Y-axis is fore-aft (along the boat)
    // - Landscape mode: X-axis is fore-aft (along the boat)
    // The phone should be placed horizontally with screen facing up
    let forwardAccel;
    if (isLandscapeRef.current) {
      // Landscape: X-axis is fore-aft
      forwardAccel = ax;
    } else {
      // Portrait: Y-axis is fore-aft  
      forwardAccel = ay;
    }
    
    const magnitude = Math.sqrt(ax * ax + ay * ay + az * az);
    
    // Apply band-pass filter (rate-detection band — narrow in Mode 2)
    const filtered = bandPassFilterRef.current 
      ? bandPassFilterRef.current.process(forwardAccel)
      : forwardAccel;

    // ── Rhythm Ratio (RHY) phase detection ───────────────────────────────
    // Uses a SEPARATE, wider-band filter so the catch impulse keeps its
    // shape — critical for getting drive/recovery timing right on phone IMU
    // data. Plus an adaptive amplitude envelope drives hysteresis bands
    // ±20 % of the recent peak so small ripples around zero don't trigger
    // spurious catches/finishes.
    const phaseFiltered = phaseFilterRef.current
      ? phaseFilterRef.current.process(forwardAccel)
      : forwardAccel;
    // Update amplitude envelope (peak hold with slow decay over ~5 strokes).
    const absPhase = Math.abs(phaseFiltered);
    if (absPhase > phaseAmpEnvelopeRef.current) {
      phaseAmpEnvelopeRef.current = absPhase;
    } else {
      // Decay by 0.5 % per sample → ~2 s to halve, so envelope tracks recent
      // amplitude without latching on a single spike.
      phaseAmpEnvelopeRef.current *= 0.995;
    }
    // Hysteresis thresholds — must exit the dead-band before a crossing
    // counts. Floor at 0.15 m/s² so the detector doesn't fire on dead-flat
    // signals (phone on table, paused mid-water etc.).
    const hyst = Math.max(0.15, phaseAmpEnvelopeRef.current * 0.20);
    const prevF = prevFilteredRef.current;
    const HALF_PERIOD_MIN_MS = 200;
    if (prevF < -hyst && phaseFiltered >= hyst) {
      // positive crossing — CATCH
      if (now - lastCatchTRef.current > HALF_PERIOD_MIN_MS) {
        // If we have a complete stroke (catch → finish → next catch), record it
        if (lastFinishTRef.current > lastCatchTRef.current && lastCatchTRef.current > 0) {
          const drive = lastFinishTRef.current - lastCatchTRef.current;
          const recovery = now - lastFinishTRef.current;
          if (drive > 0 && recovery > 0) {
            recentStrokesRef.current.push({ drive, recovery });
            if (recentStrokesRef.current.length > 8) recentStrokesRef.current.shift();
            // Emit a rolling 3-stroke smoothed ratio (or per-stroke if PM5 isn't connected).
            const ratio = smoothRhythmRatio(recentStrokesRef.current, 3) ?? computeRhythmRatio(drive, recovery);
            if (ratio != null && lastEmittedCatchTRef.current !== lastCatchTRef.current) {
              lastEmittedCatchTRef.current = lastCatchTRef.current;
              try {
                window.dispatchEvent(new CustomEvent('crewz-rhythm-update', {
                  detail: { ratio, driveMs: drive, recoveryMs: recovery, source: 'imu', t: now },
                }));
              } catch (_e) { /* noop */ }
              // Debug breadcrumb — enable with ?debug=1 in the URL
              try {
                if (typeof window !== 'undefined' &&
                    window.location?.search?.includes('debug=1')) {
                  // eslint-disable-next-line no-console
                  console.log(`[rhy-debug] drive=${drive}ms recovery=${recovery}ms ratio=1:${ratio.toFixed(2)} envelope=${phaseAmpEnvelopeRef.current.toFixed(2)} hyst=${hyst.toFixed(2)}`);
                }
              } catch (_e) {}
            }
          }
        }
        lastCatchTRef.current = now;
      }
    } else if (prevF >= hyst && phaseFiltered < -hyst) {
      // negative crossing — FINISH
      if (now - lastFinishTRef.current > HALF_PERIOD_MIN_MS) {
        lastFinishTRef.current = now;
      }
    }
    prevFilteredRef.current = phaseFiltered;
    // Live sample for the Acceleration Curve graph (wide-band filter keeps the catch shape)
    // GRAPH ONLY: raw forward accel → Acceleration Curve buffer. Never feeds stroke rate / count.
    try {
      window.dispatchEvent(new CustomEvent('crewz-accel-raw', {
        detail: { t: now, a: forwardAccel, rate: strokeRateForGraphRef.current, source: 'phone' },
      }));
    } catch (_e) { /* noop */ }
    
    // Add to buffers
    accelBufferRef.current.push(forwardAccel);
    filteredBufferRef.current.push(filtered);
    timestampBufferRef.current.push(now);
    
    // Maintain buffer size
    if (accelBufferRef.current.length > BUFFER_SIZE) {
      accelBufferRef.current.shift();
      filteredBufferRef.current.shift();
      timestampBufferRef.current.shift();
    }
    
    // Process every ~500ms
    if (now - lastRateUpdateRef.current > 500) {
      lastRateUpdateRef.current = now;
      processMotionData();
    }
  }, [processMotionData]);

  /**
   * Start tracking
   */
  const startTracking = useCallback(async () => {
    console.log('🚣 Starting hybrid stroke detection...');
    
    // Reset state
    accelBufferRef.current = [];
    filteredBufferRef.current = [];
    timestampBufferRef.current = [];
    gpsSpeedHistoryRef.current = [];
    strokeCountRef.current = 0;
    strokeCounterRef.current.reset();
    lastStrokeTimeRef.current = Date.now();
    lastRateUpdateRef.current = 0;
    detectorRef.current.reset();
    gpsCheckRef.current.reset();
    // Reset rhythm-ratio detection state
    lastCatchTRef.current = 0;
    lastFinishTRef.current = 0;
    lastEmittedCatchTRef.current = 0;
    recentStrokesRef.current = [];
    prevFilteredRef.current = 0;
    
    // Reset filter
    if (bandPassFilterRef.current) {
      bandPassFilterRef.current.reset();
    }
    // Reset the dedicated phase-detection filter + amplitude envelope so
    // each session starts from a clean baseline.
    if (phaseFilterRef.current) {
      phaseFilterRef.current.reset();
    }
    phaseAmpEnvelopeRef.current = 0;
    
    setTotalStrokes(0);
    setStrokeRate(0);
    
    // Set tracking ref BEFORE adding event listener
    isTrackingRef.current = true;
    setIsTracking(true);
    
    // Check for DeviceMotion support
    if (typeof DeviceMotionEvent !== 'undefined') {
      // iOS 13+ requires permission
      if (typeof DeviceMotionEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceMotionEvent.requestPermission();
          if (permission === 'granted') {
            setHasMotionSensors(true);
            window.addEventListener('devicemotion', handleMotion, { passive: true });
            console.log('✅ Motion sensors enabled (iOS)');
          } else {
            console.log('❌ Motion sensor permission denied');
            setHasMotionSensors(false);
            isTrackingRef.current = false;
            setIsTracking(false);
          }
        } catch (err) {
          console.log('❌ Motion sensor permission error:', err);
          setHasMotionSensors(false);
          isTrackingRef.current = false;
          setIsTracking(false);
        }
      } else {
        // Android / non-iOS
        window.addEventListener('devicemotion', handleMotion, { passive: true });
        console.log('✅ Motion sensors enabled (Android)');
        
        // Verify data after delay
        setTimeout(() => {
          if (accelBufferRef.current.length > 0) {
            setHasMotionSensors(true);
            console.log(`✅ Receiving motion data: ${accelBufferRef.current.length} samples`);
          } else {
            console.log('⚠️ No motion data received');
            setHasMotionSensors(false);
          }
        }, 2000);
      }
    } else {
      console.log('❌ DeviceMotion API not supported');
      setHasMotionSensors(false);
      isTrackingRef.current = false;
      setIsTracking(false);
    }
  }, [handleMotion]);

  // Rowing-detector GPS check: speed > 1.0 m/s for 3 s, accuracy ≤ 10 m fixes only (phone mode).
  useEffect(() => {
    if (!isTracking || !isPhoneOnly || !navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition((p) => {
      gpsCheckRef.current.onFix(Date.now(), p.coords.speed, p.coords.accuracy);
    }, () => {}, { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
    return () => { navigator.geolocation.clearWatch(id); gpsCheckRef.current.reset(); };
  }, [isTracking, isPhoneOnly]);

  /**
   * Stop tracking
   */
  useEffect(() => () => window.removeEventListener('devicemotion', handleMotion), [handleMotion]);

  const stopTracking = useCallback(() => {
    console.log('🛑 Stopping stroke detection');
    isTrackingRef.current = false;
    setIsTracking(false);
    window.removeEventListener('devicemotion', handleMotion);
  }, [handleMotion]);

  /**
   * Update GPS speed for validation
   */
  const updateGPSSpeed = useCallback((speed) => {
    if (typeof speed === 'number' && !isNaN(speed)) {
      gpsSpeedHistoryRef.current.push(speed);
      // Keep last 20 samples
      if (gpsSpeedHistoryRef.current.length > 20) {
        gpsSpeedHistoryRef.current.shift();
      }
    }
  }, []);

  /**
   * Reset stroke count
   */
  const resetStrokes = useCallback(() => {
    strokeCountRef.current = 0;
    strokeCounterRef.current.reset();
    setTotalStrokes(0);
    lastStrokeTimeRef.current = Date.now();
  }, []);

  /**
   * Manually register a stroke (e.g., from PM5)
   */
  const registerStroke = useCallback(() => {
    strokeCountRef.current += 1;
    setTotalStrokes(strokeCountRef.current);
    lastStrokeTimeRef.current = Date.now();
  }, []);

  /**
   * Update stroke rate from GPS speed (fallback)
   */
  const updateFromGPSSpeed = useCallback((speed, boatClass) => {
    // This can be used as additional validation
    updateGPSSpeed(speed);
  }, [updateGPSSpeed]);

  // ── CrewZ case IMU stream (forward-compat) ────────────────────────────────
  // Firmware (ST LSM6DSO @ 200 Hz) emits pre-computed stroke metrics over BLE.
  // Detail shape: { strokeRate, totalStrokes, strokeLength?, checkFactor?, timestamp? }
  // When the case is connected we trust its values directly and bypass phone
  // motion processing - the LSM6DSO sees the actual hull motion rather than
  // the rower's phone bouncing in a pocket.
  const crewzActiveRef = useRef(false);
  const [crewzActive, setCrewzActive] = useState(false);
  const crewzStaleTimerRef = useRef(null);
  useEffect(() => {
    const handleCrewZIMU = (event) => {
      const d = event.detail || {};
      // GRAPH ONLY: optional raw IMU X axis → Acceleration Curve buffer (does not touch stroke rate).
      const pushRaw = (t, x) => {
        if (Number.isFinite(x)) {
          window.dispatchEvent(new CustomEvent('crewz-accel-raw', { detail: { t, a: x, rate: d.strokeRate || 0, source: 'case' } }));
        }
      };
      if (Array.isArray(d.accelXSamples)) d.accelXSamples.forEach((p) => pushRaw(p.t || Date.now(), p.x));
      else if (typeof d.accelX === 'number') pushRaw(d.timestamp || Date.now(), d.accelX);
      if (typeof d.strokeRate === 'number' && d.strokeRate >= 0 && d.strokeRate <= MAX_STROKE_RATE) {
        setStrokeRate(d.strokeRate);
      }
      if (typeof d.totalStrokes === 'number' && d.totalStrokes >= 0) {
        setTotalStrokes(d.totalStrokes);
        strokeCountRef.current = d.totalStrokes;
      }
      crewzActiveRef.current = true;
      setCrewzActive(true);
      if (crewzStaleTimerRef.current) clearTimeout(crewzStaleTimerRef.current);
      crewzStaleTimerRef.current = setTimeout(() => {
        crewzActiveRef.current = false;
        setCrewzActive(false);
      }, 4000);
    };
    window.addEventListener('crewz-imu-update', handleCrewZIMU);
    return () => {
      window.removeEventListener('crewz-imu-update', handleCrewZIMU);
      if (crewzStaleTimerRef.current) clearTimeout(crewzStaleTimerRef.current);
    };
  }, []);

  return {
    // State
    strokeRate,
    totalStrokes,
    isTracking,
    hasAccelerometer: hasMotionSensors,
    hasMotionSensors,
    /** True while CrewZ-case IMU readings have arrived in the last 4 s. */
    crewzActive,
    debugInfo,
    
    // Controls
    startTracking,
    stopTracking,
    resetStrokes,
    registerStroke,
    updateFromGPSSpeed,
    updateGPSSpeed
  };
}

export default useStrokeDetection;
